import { Combobox, OverflowText, getColorFromCssVariable } from "ui";
import { BarChartCard } from "./shared/BarChartCard";
import { useErrorCodeFilter } from "../DatadogAnalytics/useErrorCodeFilter";
import { useMemo, useEffect } from "react";
import { useDatadogQuery } from "../DatadogAnalytics/useDatadogQuery";
import { useDeclineTypeFilter } from "../DatadogAnalytics/useDeclineTypeFilter";
import { ChevronDownIcon } from "icons/outline";

const ERROR_CODE_COLOR_VARS = [
  "--chart-categorical-6-subtler",
  "--chart-categorical-7-subtler",
  "--chart-categorical-5-subtler",
  "--chart-categorical-4-subtler",
];
const OTHERS_COLOR_VAR = "--chart-categorical-8-subtler";

type DailyDeclinesData = Record<
  string,
  { label: string; value: number; type: "SOFT_DECLINE" | "HARD_DECLINE" }[]
>;

export function TopErrorCodesDaily({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<DailyDeclinesData>({
      chartType: "DAILY_ERROR_CODES_COUNTS",
      delayMs: 2100,
    });

  const { selectedType, setSelectedType, typeOptions } = useDeclineTypeFilter();
  const selectedDeclineType = selectedType[0];

  const { labels, errorCodeData, allDeclineReasons } = useMemo(() => {
    if (data && Object.keys(data).length > 0) {
      const labels = Object.keys(data);

      const filteredByType: DailyDeclinesData = {};
      labels.forEach((time) => {
        const entries = data[time];
        filteredByType[time] =
          selectedDeclineType === "ALL_TYPES"
            ? entries
            : entries.filter((entry) => entry.type === selectedDeclineType);
      });

      // Build errorCodeData: { error_code: [count_per_day] } and totals
      const errorCodeData: Record<string, number[]> = {};
      const errorCodeTotals: Record<string, number> = {};

      labels.forEach((time, dayIndex) => {
        filteredByType[time].forEach(({ label, value }) => {
          if (!errorCodeData[label]) {
            errorCodeData[label] = new Array(labels.length).fill(0);
            errorCodeTotals[label] = 0;
          }
          errorCodeData[label][dayIndex] = value;
          errorCodeTotals[label] += value;
        });
      });

      // Create allDeclineReasons sorted by value (highest to lowest)
      const allDeclineReasons = Object.entries(errorCodeTotals)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);

      return {
        labels,
        errorCodeData,
        allDeclineReasons,
      };
    }

    return {
      labels: [] as string[],
      errorCodeData: {} as Record<string, number[]>,
      allDeclineReasons: [] as { label: string; value: number }[],
    };
  }, [data, selectedDeclineType]);

  const {
    selectedErrorCodes,
    setSelectedErrorCodes,
    errorCodeOptions,
    filteredData,
    hasOthers,
    isItemDisabled,
  } = useErrorCodeFilter(allDeclineReasons);

  // Reset error codes when decline type changes
  useEffect(() => {
    setSelectedErrorCodes([]);
  }, [selectedDeclineType, setSelectedErrorCodes]);

  // Build datasets from filtered data
  const datasets = useMemo(() => {
    const baseDatasets = filteredData
      .filter((item) => item.label !== "OTHERS")
      .map((item, index) => {
        const colorVar =
          ERROR_CODE_COLOR_VARS[index % ERROR_CODE_COLOR_VARS.length];
        const color = getColorFromCssVariable(colorVar);
        return {
          label: item.label,
          data: errorCodeData[item.label] || [],
          backgroundColor: color,
          borderColor: color,
          stack: "stack0",
        };
      });

    // Add OTHERS dataset if needed
    if (hasOthers) {
      const selectedLabels = filteredData
        .filter((item) => item.label !== "OTHERS")
        .map((item) => item.label);
      const unselectedCodes = Object.keys(errorCodeData).filter(
        (code) => !selectedLabels.includes(code)
      );

      const othersData = labels.map((_, dayIndex) => {
        return unselectedCodes.reduce((sum, code) => {
          const codeData = errorCodeData[code];
          return sum + (codeData?.[dayIndex] || 0);
        }, 0);
      });

      const othersColor = getColorFromCssVariable(OTHERS_COLOR_VAR);
      baseDatasets.push({
        label: "OTHERS",
        data: othersData,
        backgroundColor: othersColor,
        borderColor: othersColor,
        stack: "stack0",
      });
    }

    return baseDatasets;
  }, [filteredData, errorCodeData, hasOthers, labels]);

  return (
    <BarChartCard
      ref={ref}
      title={title}
      description={description}
      labels={labels}
      datasets={datasets}
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
      headerAction={
        <div className="flex gap-2">
          <Combobox
            mode="popup"
            label={selectedType[1]}
            options={typeOptions}
            selected={selectedType}
            onSelect={(option) => {
              if (option) setSelectedType(option);
            }}
            className="w-auto"
            align="start"
          />
          <Combobox
            mode="popup"
            label="All error codes"
            placeholder="Search error codes"
            options={errorCodeOptions}
            selected={selectedErrorCodes}
            onSelect={setSelectedErrorCodes}
            multiple
            withConfirmation
            className="w-40"
            isItemDisabled={isItemDisabled}
            align="start"
            disableSelectAll
            renderTrigger={(triggerProps) => (
              <button type="button" {...triggerProps}>
                <span className="min-w-0 flex-1 truncate text-left text-foundations-bolder">
                  {selectedErrorCodes.length > 0
                    ? `${selectedErrorCodes.length} error${
                        selectedErrorCodes.length > 1 ? "s" : ""
                      } selected`
                    : "All error codes"}
                </span>
                <ChevronDownIcon className="ml-2 size-5 shrink-0 text-foundations-subtle transition-transform group-data-[popup-open]:rotate-180" />
              </button>
            )}
            renderOption={(label) => (
              <OverflowText variant="light">{label}</OverflowText>
            )}
            popupClassName="w-72"
          />
        </div>
      }
    />
  );
}
